import React, { useState } from 'react';
import { X, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = (prefillFocus = '') => ({
  family_name: '',
  contact_email: '',
  contact_phone: '',
  number_of_children: '',
  current_location: '',
  travel_destinations: '',
  preferred_start: '',
  engagement_duration: '',
  subjects_focus: prefillFocus,
  budget_range: '',
  accommodation_provided: 'To discuss',
  additional_notes: ''
});

const TravelEnquiryModal = ({ open, onClose, prefillFocus = '' }) => {
  const [form, setForm] = useState(initialForm(prefillFocus));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm(initialForm(prefillFocus));
      setSuccess(false);
    }
  }, [open, prefillFocus]);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/travel-enquiry`, form);
      setSuccess(true);
      toast.success('Enquiry received. Dr. Omakobia will respond within 48 hours.');
      setForm(initialForm());
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again or email drmomakobia@abbaacademy.co.uk directly.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setSuccess(false);
    setForm(initialForm());
    window.dispatchEvent(new CustomEvent('close-travel-enquiry'));
    onClose();
  };

  const labelStyle = {
    display: 'block',
    color: '#0F172A',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    fontFamily: "'Inter', sans-serif"
  };
  const inputStyle = {
    width: '100%',
    padding: '0.85rem 1rem',
    border: '1px solid rgba(15, 23, 42, 0.15)',
    borderRadius: '3px',
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    color: '#0F172A',
    background: '#FFFCF5',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  };
  const focusStyle = (e) => { e.target.style.borderColor = '#C5A059'; };
  const blurStyle = (e) => { e.target.style.borderColor = 'rgba(15, 23, 42, 0.15)'; };

  return (
    <div
      data-testid="travel-enquiry-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: '2rem 1rem',
        overflow: 'auto'
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFCF5', width: '100%', maxWidth: '720px',
          borderRadius: '4px', boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
          border: '1px solid #C5A059',
          position: 'relative', marginTop: '2rem', marginBottom: '2rem'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#0F172A', padding: '2rem 2.5rem',
          borderBottom: '2px solid #C5A059', position: 'relative'
        }}>
          <button
            onClick={close}
            data-testid="close-enquiry-modal"
            aria-label="Close"
            style={{
              position: 'absolute', top: '1rem', right: '1rem',
              background: 'transparent', border: 'none',
              color: '#FFFCF5', cursor: 'pointer', padding: '0.5rem'
            }}
          >
            <X size={22} />
          </button>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: '#C5A059',
            marginBottom: '0.75rem', fontFamily: "'Inter', sans-serif"
          }}>
            <Shield size={12} />
            Strictly Confidential
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: '#FFFCF5', fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700, margin: 0, lineHeight: 1.2, letterSpacing: '-0.02em'
          }}>
            In-Residence Travel Tutor <span style={{ color: '#C5A059', fontStyle: 'italic' }}>Enquiry</span>
          </h2>
          <p style={{
            color: 'rgba(255,252,245,0.7)', margin: '0.5rem 0 0 0',
            fontSize: '0.9rem', fontFamily: "'Inter', sans-serif"
          }}>
            Every enquiry is read personally by Dr. Omakobia. Response within 48 hours.
          </p>
        </div>

        {/* Body */}
        {success ? (
          <div style={{ padding: '4rem 2.5rem', textAlign: 'center' }}>
            <CheckCircle2 size={64} color="#C5A059" style={{ marginBottom: '1.5rem' }} />
            <h3 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '1.75rem', color: '#0F172A', marginBottom: '1rem',
              fontWeight: 700
            }}>
              Thank you.
            </h3>
            <p style={{
              color: '#475569', fontSize: '1rem', lineHeight: 1.7,
              maxWidth: '440px', margin: '0 auto 2rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Your confidential enquiry has been received. Dr. Omakobia will respond to you
              personally within 48 hours. A confirmation email has been sent to your inbox.
            </p>
            <button
              onClick={close}
              data-testid="enquiry-success-close"
              style={{
                background: '#0F172A', color: '#FFFCF5',
                padding: '0.9rem 2rem', border: 'none', cursor: 'pointer',
                fontFamily: "'Inter', sans-serif", fontWeight: 600,
                letterSpacing: '0.02em', borderRadius: '3px'
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: '2.5rem', display: 'grid', gap: '1.5rem' }}>
            {/* Row 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Family / Contact Name *</label>
                <input required data-testid="input-family-name" style={inputStyle} value={form.family_name}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('family_name')} placeholder="Mrs. Smith" />
              </div>
              <div>
                <label style={labelStyle}>Contact Email *</label>
                <input required type="email" data-testid="input-contact-email" style={inputStyle} value={form.contact_email}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('contact_email')} placeholder="you@example.com" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Phone / WhatsApp (optional)</label>
                <input data-testid="input-contact-phone" style={inputStyle} value={form.contact_phone}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('contact_phone')} placeholder="+44 ..." />
              </div>
              <div>
                <label style={labelStyle}>Children & Ages *</label>
                <input required data-testid="input-children" style={inputStyle} value={form.number_of_children}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('number_of_children')} placeholder="e.g. 2 (ages 11, 14)" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Current Location *</label>
                <input required data-testid="input-location" style={inputStyle} value={form.current_location}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('current_location')} placeholder="London, UK" />
              </div>
              <div>
                <label style={labelStyle}>Expected Travel Destinations</label>
                <input data-testid="input-travel-destinations" style={inputStyle} value={form.travel_destinations}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('travel_destinations')} placeholder="e.g. Jeddah, Monaco, Aspen" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Preferred Start *</label>
                <input required data-testid="input-start" style={inputStyle} value={form.preferred_start}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('preferred_start')} placeholder="e.g. August 2026" />
              </div>
              <div>
                <label style={labelStyle}>Duration *</label>
                <input required data-testid="input-duration" style={inputStyle} value={form.engagement_duration}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('engagement_duration')} placeholder="e.g. 5 months / long-term" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Focus Areas / Subjects *</label>
              <input required data-testid="input-focus" style={inputStyle} value={form.subjects_focus}
                onFocus={focusStyle} onBlur={blurStyle} onChange={update('subjects_focus')} placeholder="e.g. 13+ prep, Maths, Science, executive functioning, confidence" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Budget Range *</label>
                <input required data-testid="input-budget" style={inputStyle} value={form.budget_range}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('budget_range')} placeholder="e.g. £80k-£150k p.a. / negotiable" />
              </div>
              <div>
                <label style={labelStyle}>Accommodation Provided *</label>
                <select required data-testid="input-accommodation" style={inputStyle} value={form.accommodation_provided}
                  onFocus={focusStyle} onBlur={blurStyle} onChange={update('accommodation_provided')}>
                  <option>Yes — private quarters in family home</option>
                  <option>Yes — nearby residence provided</option>
                  <option>No — tutor to arrange own</option>
                  <option>To discuss</option>
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Additional Context (optional)</label>
              <textarea data-testid="input-notes" rows="4" style={{ ...inputStyle, resize: 'vertical' }} value={form.additional_notes}
                onFocus={focusStyle} onBlur={blurStyle} onChange={update('additional_notes')}
                placeholder="Anything Dr. Omakobia should know — the child's learning profile, prior tutoring, family goals, or specific requirements..." />
            </div>

            <div style={{
              padding: '1rem 1.25rem',
              background: 'rgba(197, 160, 89, 0.08)',
              borderLeft: '3px solid #C5A059',
              fontSize: '0.85rem',
              color: '#475569',
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1.6,
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start'
            }}>
              <Shield size={16} color="#C5A059" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                Your enquiry is handled in strict confidence and is not shared with third parties.
                Only Dr. Omakobia will read the details you have provided.
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-enquiry-btn"
              style={{
                background: submitting ? '#94A3B8' : '#0F172A',
                color: '#FFFCF5', padding: '1.1rem 2rem', border: 'none',
                cursor: submitting ? 'wait' : 'pointer',
                fontFamily: "'Inter', sans-serif", fontWeight: 700,
                fontSize: '0.95rem', letterSpacing: '0.05em',
                textTransform: 'uppercase', borderRadius: '3px',
                display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => { if (!submitting) { e.currentTarget.style.background = '#C5A059'; e.currentTarget.style.color = '#0F172A'; } }}
              onMouseLeave={(e) => { if (!submitting) { e.currentTarget.style.background = '#0F172A'; e.currentTarget.style.color = '#FFFCF5'; } }}
            >
              {submitting ? 'Sending...' : 'Submit Confidential Enquiry'}
              {!submitting && <ArrowRight size={18} />}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TravelEnquiryModal;
